/**
 * Formats a phone number to (XXX) XXX-XXXX format (or (XXX) XXX-XXX for 9-digit numbers)
 * This matches the backend PhoneFormatter.FormatPhoneNumber implementation for consistency.
 * @param phone - The phone number string
 * @returns Formatted phone number or empty string if invalid
 */
export const formatPhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Check if it's a valid 10-digit number
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  // Check if it's a valid 11-digit number starting with 1
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  
  // Handle 9-digit numbers - format as (XXX) XXX-XXX to match standard format
  if (cleaned.length === 9) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  // Return original if it doesn't match expected formats
  return phone;
};

/**
 * Applies phone number input mask as user types
 * @param value - The input value
 * @returns Masked phone number
 */
export const applyPhoneMask = (value: string): string => {
  // Remove all non-digit characters
  const cleaned = value.replace(/\D/g, '');
  
  // Apply mask based on length
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return `(${cleaned}`;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  if (cleaned.length <= 10) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  
  // Limit to 10 digits
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

/**
 * Gets a relative time string (e.g., "2 hours ago", "3 days ago")
 * @param dateString - The date string to format
 * @returns Relative time string
 */
export const getRelativeTime = (dateString: string): string => {
  // Ensure the date string is properly parsed as UTC if it doesn't have timezone info
  let date: Date;
  if (dateString.includes('Z') || dateString.includes('+') || dateString.includes('-', 10)) {
    // Already has timezone info
    date = new Date(dateString);
  } else {
    // Assume UTC if no timezone info
    date = new Date(dateString + 'Z');
  }
  
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

/**
 * Formats a date string to MM/DD/YYYY format
 * @param dateString - The date string to format
 * @returns Formatted date string
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Formats a date string with proper timezone handling
 * Treats server time as UTC if no timezone is specified
 * @param dateString - The date string to format
 * @param companyTimeZone - Optional company timezone to use instead of browser timezone
 * @returns Object with date, time, and relative time strings
 */
export const formatTimestamp = (dateString: string, companyTimeZone?: string) => {
  if (!dateString) {
    return { date: '', time: '', relative: '' };
  }
  
  // Ensure we interpret the server time as UTC. If it lacks a timezone, append 'Z'.
  const isoString = /Z|[\+\-]\d{2}:?\d{2}$/.test(dateString) ? dateString : `${dateString}Z`;
  const utcDate = new Date(isoString);

  // Use company timezone if provided, otherwise use browser's local timezone
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  };

  const dateOptions: Intl.DateTimeFormatOptions = {};

  if (companyTimeZone) {
    options.timeZone = companyTimeZone;
    dateOptions.timeZone = companyTimeZone;
  }

  return {
    date: utcDate.toLocaleDateString(undefined, dateOptions),
    time: utcDate.toLocaleTimeString([], options),
    relative: getRelativeTime(utcDate.toISOString()),
    timezone: companyTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
  };
};

/**
 * Formats a number as currency (USD)
 * @param value - The number to format
 * @returns Formatted currency string
 */
export const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '';
  return value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

/**
 * Parses a currency string back to a number
 * @param value - The currency string to parse
 * @returns Parsed number or null
 */
export const parseCurrency = (value: string): number | null => {
  if (!value) return null;
  const cleaned = value.replace(/[^0-9.]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
};

/**
 * Constructs the full URL for a logo from the relative path returned by the backend
 * @param logoPath - The relative logo path from the backend (e.g., "company_1/logos/guid.jpg")
 * @returns Full URL to the logo image
 */
export const getLogoUrl = (logoPath: string | null | undefined): string => {
  if (!logoPath) return '';
  
  // If it's already a full URL, return as-is
  if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
    return logoPath;
  }
  
  // Use the API endpoint for logos which has proper CORS headers
  const backendBaseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || `${window.location.protocol}//${window.location.hostname}:5269`;
  return `${backendBaseUrl}/api/companies/logo/${logoPath}`;
};

/**
 * Constructs the full URL for a profile photo from the relative path returned by the backend
 * @param photoPath - The relative photo path from the backend (e.g., "company_1/users/profile_photos/guid.jpg")
 * @returns Full URL to the profile photo image
 */
export const getProfilePhotoUrl = (photoPath: string | null | undefined): string => {
  // Profile photos are stored via FileStorageService (blob or filesystem),
  // same as company logos — use the logo API endpoint to serve them.
  return getLogoUrl(photoPath);
};

/**
 * Constructs the full URL for an equipment photo from the relative path returned by the backend
 * @param photoPath - The relative photo path from the backend (e.g., "company_1/equipment/1/images/guid.jpg")
 * @returns Full URL to the equipment photo image
 */
export const getEquipmentPhotoUrl = (photoPath: string | null | undefined): string => {
  if (!photoPath) return '';
  
  // If it's already a full URL, return as-is
  if (photoPath.startsWith('http://') || photoPath.startsWith('https://')) {
    return photoPath;
  }
  
  // Use the API endpoint for equipment photos
  const backendBaseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || `${window.location.protocol}//${window.location.hostname}:5269`;
  return `${backendBaseUrl}/api/equipment/photo/${photoPath}`;
};

/**
 * Capitalizes the first letter of each word in a string
 * @param str - The string to capitalize
 * @returns String with each word capitalized
 */
export const capitalizeWords = (str: string): string => {
  if (!str) return '';
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};
